import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AuthProvider, { useAuth } from './components/Auth/AuthProvider';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AdminRoute from './components/Auth/AdminRoute';
import SuperadminRoute from './components/Auth/SuperadminRoute';
import InactivityWarningModal from './components/Auth/InactivityWarningModal';
import TenantErrorBoundary from './components/Auth/TenantErrorBoundary';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import SignupInvitation from './components/Auth/SignupInvitation';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import AuthCallback from './components/Auth/AuthCallback';
import LandingPage from './components/Landing/LandingPage';
import AcceptInvitation from './components/AcceptInvitation';
import EmailConfirmed from './components/Auth/EmailConfirmed';
import BootstrapSuperadmin from './components/Auth/BootstrapSuperadmin';
import Layout from './components/Layout/Layout';
import WorkflowDashboard from './components/WorkflowDashboard';
import RecruitmentWorkflow from './components/RecruitmentWorkflow';
import OnboardingWorkflow from './components/OnboardingWorkflow';
import CoreLifecycle from './components/CoreLifecycle';
import PerformanceManagement from './components/PerformanceManagement';
import EmployeeRelations from './components/EmployeeRelations';
import ExitProcesses from './components/ExitProcesses';
import TribunalPrep from './components/TribunalPrep';
import ComplianceTracker from './components/ComplianceTracker';
import { PlatformAdminLayout, PlatformDashboard, TenantManagement, UserManagement, PlatformMetrics, AuditLog, MfaSetup } from './components/PlatformAdmin';
import CaseManagement from './components/CaseManagement';
import CaseDetails from './components/CaseDetails';
import CompanyWorkflows from './components/CompanyWorkflows';
import WorkflowDetails from './components/WorkflowDetails';
import StaffManagement from './components/StaffManagement';
import StaffDetails from './components/StaffDetails';
import TeamManagement from './components/TeamManagement';
import UserProfile from './components/UserProfile';
import * as FiIcons from 'react-icons/fi';
import './App.css';

import ScrollToTop from './components/common/ScrollToTop';

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const { showInactivityWarning, timeUntilLogout, extendSession, signOut } = useAuth();

  return (
    <>
      <Router>
        <ScrollToTop />
        <TenantErrorBoundary>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/signup-invitation" element={<SignupInvitation />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
            <Route path="/email-confirmed" element={<EmailConfirmed />} />
            <Route path="/bootstrap-admin" element={<BootstrapSuperadmin />} />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <WorkflowDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team"
              element={
                <AdminRoute>
                  <Layout>
                    <TeamManagement />
                  </Layout>
                </AdminRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <UserProfile />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/cases"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CaseManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/case/:caseId"
              element={
                <ProtectedRoute>
                  <Layout
                    breadcrumbItems={[
                      { name: 'Employee Cases', path: '/cases', icon: FiIcons.FiFolder },
                      { name: 'Case Details', path: '', icon: FiIcons.FiFileText }
                    ]}
                  >
                    <CaseDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/company-workflows"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CompanyWorkflows />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/workflow/:workflowId"
              element={
                <ProtectedRoute>
                  <Layout
                    breadcrumbItems={[
                      { name: 'Company Workflows', path: '/company-workflows', icon: FiIcons.FiLayers },
                      { name: 'Workflow Details', path: '', icon: FiIcons.FiTarget }
                    ]}
                  >
                    <WorkflowDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff"
              element={
                <ProtectedRoute>
                  <Layout>
                    <StaffManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff/:staffId"
              element={
                <ProtectedRoute>
                  <Layout
                    breadcrumbItems={[
                      { name: 'Staff Management', path: '/staff', icon: FiIcons.FiUsers },
                      { name: 'Staff Details', path: '', icon: FiIcons.FiUser }
                    ]}
                  >
                    <StaffDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/recruitment"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RecruitmentWorkflow />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <Layout>
                    <OnboardingWorkflow />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/core-lifecycle"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CoreLifecycle />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/performance"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PerformanceManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee-relations"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EmployeeRelations />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/exit-processes"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ExitProcesses />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tribunal-prep"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TribunalPrep />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/compliance"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ComplianceTracker />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Platform Admin Routes (Superadmin Only) */}
            <Route
              path="/platform-admin"
              element={
                <SuperadminRoute>
                  <PlatformAdminLayout />
                </SuperadminRoute>
              }
            >
              <Route index element={<PlatformDashboard />} />
              <Route path="metrics" element={<PlatformMetrics />} />
              <Route path="tenants" element={<TenantManagement />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="audit" element={<AuditLog />} />
              <Route path="mfa" element={<MfaSetup />} />
            </Route>
          </Routes>
        </TenantErrorBoundary>
      </Router>

      {/* Inactivity Warning Modal */}
      <InactivityWarningModal
        isOpen={showInactivityWarning}
        timeRemaining={timeUntilLogout}
        onExtend={extendSession}
        onLogout={signOut}
      />
    </>
  );
}

export default App;